// src/routes/JobPostCreation.js

import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Container,
  InputAdornment,
  Chip,
  Fab
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import styled from '@mui/material/styles/styled';
import {
  Refresh as RefreshIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
  Forum as ForumIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  RestartAlt as RestartAltIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  InfoOutlined as InfoOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  AutoAwesome as AutoAwesomeIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { styled as muiStyled } from '@mui/system';
import RequirementsCard from './RequirementsCard';
import EditRequirementsCard from './EditRequirementsCard';

import AppLayout from '../../components/layout/AppLayout';
import LoadingScreen from '../../components/LoadingScreen';
import AdviceContentBox from '../../components/AdviceContentBox';

// ★ AIチャットコンポーネント
import AIChat from '../../components/AIChat';
import AIChatSide from '../../components/AIChatSide';

// API Client
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { segmentedTabsContainer, segmentedTab, chatIconButton, menuIconButton, selectionCard, selectionCardButton, designTokens } from '../../theme/designTokens';

const jobPostTheme = designTokens.featureThemes.job_posting;

// Markdown support
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// 専用アシストメニュー
import AssistMenu from './AssistMenu';
import { ThemeProvider } from '@mui/material/styles';
import MainContent from './MainContent';
import InitialFormScreen from './InitialFormScreen';
import { activityTracker } from '../../utils/activityTracker';
import { showError, showSuccess, showInfo } from '../../utils/errorHandler';
import { bg, text as textColors, border, shadow, gradient } from '../../utils/darkModeAdapter';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/** フォームで入力する求人要件 （必要に応じてフィールドを追加）*/
const defaultRequirements = {
  // 基本
  role: '',      // 職種 (必須)
  skills: '',    // 必要スキル (必須)
  salary: '',    // 報酬レンジ (必須)
  pr: '',        // 企業PRポイント
  location: '',  // 勤務地
  welfare: '',   // 福利厚生

  // 追加項目(例)
  publishStart: '', // 求人掲載開始日
  publishEnd: '',   // 求人掲載終了日
  pastExamples: ''  // 過去の求人テンプレ・参考文面
};

// ローカルストレージのキー
const STORAGE_KEY = 'jobPostCreationData';

// ============= セグメントコントロール型Tabs =============
const CustomTabs = styled(Tabs)(({ theme }) => ({
  ...segmentedTabsContainer('job_posting'),
}));

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    ...segmentedTab('job_posting'),
  })
);

function NiceTabsExample({ tabValue, setTabValue }) {
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  return (
    <CustomTabs
      value={tabValue}
      onChange={handleTabChange}
      variant="fullWidth"
      textColor="inherit"
      indicatorColor="secondary"
    >
      <CustomTab label="求人要件" />
      <CustomTab label="生成された求人票" />
    </CustomTabs>
  );
}

/** UIモード: 'selection' | 'form' | 'loading' | 'main' */
export default function JobPostCreation() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  // 認証とトークン管理
  const { user: currentUser, loading: authLoading } = useAuth();


  const [uiMode, setUiMode] = useState('selection'); // 初期値を'selection'に変更
  const [creationMode, setCreationMode] = useState(''); // 'new'または'existing'
  const [requirements, setRequirements] = useState(defaultRequirements);
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [advice, setAdvice] = useState(null);
  
  // 元のデータ構造との互換性のために残す
  const [jobPostMd, setJobPostMd] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDecided, setIsDecided] = useState(false); // 求人票の確定状態
  
  // 履歴管理
  const [jobPostHistory, setJobPostHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);

  // AI アドバイス
  const [jobPostAdvice, setJobPostAdvice] = useState(null);

  // AI品質チェック結果（永続化対象）
  const [aiAnalysis, setAiAnalysis] = useState(null);

  // メニュー
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isEditingRequirements, setIsEditingRequirements] = useState(false);

  // 履歴ダイアログ管理
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  // 削除確認ダイアログ
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // チャットモード管理
  const [chatMode, setChatMode] = useState('off'); // 'off', 'popup', 'sidebar'
  const [isChatOpen, setIsChatOpen] = useState(false); // チャットの表示状態を管理

  // チャット管理
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // 自動保存管理
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
  const autoSaveTimerRef = useRef(null);
  const isLoadingFromHistoryRef = useRef(false); // 履歴読み込み中フラグ（unsaved表示を抑制）
  const isSavingRef = useRef(false); // 保存完了後のsetJobPostMdによるuseEffect再トリガーを防止

  // チャットモードのラベル
  const getChatModeLabel = (mode) => {
    switch (mode) {
      case 'sidebar': return 'サイドバー';
      case 'popup': return 'ポップアップ';
      default: return 'オフ';
    }
  };

  // 初期ロード時にローカルストレージから前回のデータを読み込む
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);

        // localStorage復元時は自動保存をスキップ
        isLoadingFromHistoryRef.current = true;

        if (parsedData.requirements) setRequirements(parsedData.requirements);
        if (parsedData.jobPostMd) setJobPostMd(parsedData.jobPostMd);
        if (parsedData.sessionId) setSessionId(parsedData.sessionId);
        if (parsedData.messages) setMessages(parsedData.messages);
        if (parsedData.jobPostAdvice) setJobPostAdvice(parsedData.jobPostAdvice);
        if (parsedData.isDecided !== undefined) setIsDecided(parsedData.isDecided);
        if (parsedData.aiAnalysis) setAiAnalysis(parsedData.aiAnalysis);

        if (parsedData.jobPostHistory) {
          setJobPostHistory(parsedData.jobPostHistory);
          if (parsedData.currentHistoryIndex >= 0) {
            setCurrentHistoryIndex(parsedData.currentHistoryIndex);
          } else if (parsedData.jobPostHistory.length > 0) {
            // Default to the most recent entry if no index is saved
            setCurrentHistoryIndex(parsedData.jobPostHistory.length - 1);
          }
        }
        
        // 前回の表示モードを復元
        if (parsedData.jobPostMd) {
          setMenuAnchorEl(null); // メニューの参照をリセット
          setUiMode('main');
        }
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
  }, []);

  // データが変更されたときにローカルストレージに保存
  useEffect(() => {
    if (uiMode === 'main') {
      const dataToSave = {
        requirements,
        jobPostMd,
        sessionId,
        messages,
        jobPostHistory,
        currentHistoryIndex,
        jobPostAdvice,
        isDecided,
        aiAnalysis
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }
  }, [requirements, jobPostMd, sessionId, messages, jobPostHistory, currentHistoryIndex, jobPostAdvice, isDecided, aiAnalysis, uiMode]);

  // チャットを末尾へ自動スクロール
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Add activity tracking
  useEffect(() => {
    const cleanup = activityTracker.trackPageView('job_post');
    return () => cleanup();
  }, []);

  // 生成したAIアドバイス
  const generateAdvice = (jobPost, reqOverride) => {
    // 簡易的なアドバイス生成（本来はAPIからの応答を使用するべき）
    const req = reqOverride || requirements;
    const roleName = req.role || req.title || '指定の職種';
    const skillsText = req.skills || '';
    const salaryText = req.salary || '';

    return {
      advice: `この求人票は${roleName}に特化した内容になっています。応募者が魅力を感じる要素がしっかり含まれています。`,
      reason: skillsText
        ? `${skillsText}のスキルセットが明確に示され、${salaryText || '報酬情報'}も明示されているため、応募者は自分に合った求人か判断しやすくなっています。`
        : `${salaryText || '報酬情報'}が明示されており、応募者は自分に合った求人か判断しやすくなっています。`,
      improvement: `企業PRポイントをより具体的に記載すると、応募者の興味を引きやすくなります。また、具体的な業務内容や成長機会についてもう少し詳しく書くとより良いでしょう。`
    };
  };

  // Track job post generation
  const generateJobPostFromRequirements = async (requirementsData) => {
    // 引数で渡されたデータを使用、なければstateのrequirementsを使用
    const dataToSend = requirementsData || requirements;
    
    // roleフィールドの確認
    if (!dataToSend.role || dataToSend.role.trim() === '') {
      console.error('Error: role field is empty');
      showError(null, '職種を入力してください');
      setUiMode('form');
      return;
    }
    
    const endTracking = await activityTracker.trackFeatureUse('job_post_generate', {
      requirements: dataToSend
    });
    
    try {
      setIsLoading(true);
      setUiMode('loading');
      setError(null);
      
      // お知らせを表示
      showSnackbar("求人票を生成しています...");
      
      // API呼び出し - requirementsオブジェクトを直接送信
      const response = await axios.post(`${API_BASE_URL}/job_posting/generate`, 
        dataToSend, 
        { withCredentials: true }
      );
      
      if (!response.data || !response.data.status || response.data.status !== 'success') {
        throw new Error('APIエラー: 生成に失敗しました');
      }
      
      // 正しいデータ設定
      setSessionId(response.data.session_id);
      setJobPostMd(response.data.job_post);
      setIsDecided(false); // 新規生成時は未確定

      // 履歴に追加
      const historyItem = {
        requirements: { ...dataToSend },
        jobPostMd: response.data.job_post,
        timestamp: new Date().toISOString(),
        is_decided: false
      };
      setJobPostHistory(prev => [...prev, historyItem]);
      setCurrentHistoryIndex(jobPostHistory.length); // 新しいインデックスは更新前の配列の長さ
      
      // AIアドバイスを更新
      const advice = generateAdvice(response.data.job_post, dataToSend);
      setJobPostAdvice(advice);

      // 前回のAI品質チェック結果をクリア（新しい求人票なので）
      setAiAnalysis(null);

      // 次の画面へ
      setMenuAnchorEl(null); // メニューの参照をリセット
      setUiMode('main');

      // 成功のお知らせ
      showSnackbar("求人票を生成しました");
      
    } catch (error) {
      console.error('Error generating job post:', error);
      showError(error, '求人票の生成中にエラーが発生しました');
      setUiMode('form'); // エラー時はフォーム画面に戻す
    } finally {
      setIsLoading(false);
      endTracking();
    }
  };
  
  // 求人作成モードの選択
  const handleCreationModeSelect = (mode) => {
    setCreationMode(mode);
    
    // 過去の求人票モードの場合のみrequirementsをクリア
    if (mode === 'existing') {
      setRequirements({}); // 空オブジェクトでクリア
    }
    // 'new'モードの場合は既存のdefaultRequirementsをそのまま使用（変更なし）
    
    setUiMode('form');
  };
  
  // フォーム送信時のハンドラー
  const handleFormSubmit = (formData) => {
    if (creationMode === 'new') {
      // 新規作成: newモードのフィールドをマッピング
      const updatedRequirements = {
        mode: 'new',
        role: formData.role || '',
        skills: formData.skills || '',
        salary: formData.salary || '',
        pr: formData.companyPr || formData.pr || '',
        location: formData.location || '',
        welfare: formData.welfare || '',
        publishStart: formData.publishStart || '',
        publishEnd: formData.publishEnd || '',
        pastExamples: formData.pastExamples || ''
      };

      setRequirements(updatedRequirements);
      generateJobPostFromRequirements(updatedRequirements);
    } else if (creationMode === 'existing') {
      // 過去の求人票をもとに作成: existingモードのフィールドをマッピング
      const updatedRequirements = {
        mode: 'existing',
        title: formData.title || '',
        summary: formData.summary || '',
        jobDescription: formData.jobDescription || '',
        targetPerson: formData.targetPerson || '',
        salary: formData.salary || '',
      };

      setRequirements(updatedRequirements);
      generateJobPostFromExisting(formData);
    } else {
      console.error('Unknown creationMode:', creationMode);
      setError('作成モードが不明です。ページを再読み込みしてください。');
    }
  };
  
  // 過去の求人票をもとに生成する関数
  const generateJobPostFromExisting = async (formData) => {
    try {
      setIsLoading(true);
      setUiMode('loading');
      setError(null);
      
      // お知らせを表示
      showSnackbar("過去の求人票をもとに新しい求人を生成しています...");

      // 過去の求人票からの生成用APIエンドポイント
      const response = await axios.post(`${API_BASE_URL}/job_posting/generate_from_existing`, 
        formData, 
        { withCredentials: true }
      );
      
      if (response.data.status === 'success') {
        setSessionId(response.data.session_id);
        setJobPostMd(response.data.job_post);
        setIsDecided(false); // 過去の求人票からの生成も未確定

        // AIアドバイスを更新
        const advice = generateAdvice(response.data.job_post, formData);
        setJobPostAdvice(advice);

        // 前回のAI品質チェック結果をクリア（新しい求人票なので）
        setAiAnalysis(null);

        // 履歴に追加
        const historyItem = {
          requirements: { ...formData },
          jobPostMd: response.data.job_post,
          timestamp: new Date().toISOString(),
          is_decided: false
        };
        setJobPostHistory(prev => [...prev, historyItem]);
        setCurrentHistoryIndex(jobPostHistory.length); // 新しいインデックスは更新前の配列の長さ

        // 次の画面へ
        setMenuAnchorEl(null); // メニューの参照をリセット
        setUiMode('main');

        // 成功メッセージ
        showSnackbar("求人票を生成しました");
      } else {
        setError('求人票の生成に失敗しました。入力内容を見直してもう一度お試しください。');
        setUiMode('form');
      }
    } catch (err) {
      console.error('Error generating job post from existing:', err);
      showError(err, '求人票の生成中にエラーが発生しました');
      setUiMode('form');
    } finally {
      setIsLoading(false);
    }
  };

  // 再生成ボタン
  const handleRegenerate = () => {
    generateJobPostFromRequirements(requirements);
  };
  
  // 要件編集関連ハンドラー
  const handleRequirementsEdit = () => {
    setIsEditingRequirements(true);
  };
  
  const handleRequirementsSave = (updatedRequirements) => {
    setRequirements(updatedRequirements);
    setIsEditingRequirements(false);
    localStorage.setItem('jobPostRequirements', JSON.stringify(updatedRequirements));
    

    handleRegenerateJobPost();
  };
  
  const handleRequirementsCancel = () => {
    setIsEditingRequirements(false);
  };

  // Track job post regeneration
  const handleRegenerateJobPost = async () => {
    const endTracking = await activityTracker.trackFeatureUse('job_post_regenerate', {
      requirements,
      jobPostMd
    });
    
    try {
      setUiMode('loading');
      setIsGenerating(true);
      setError(null);
      
      // お知らせを表示
      showSnackbar("求人票を再生成しています...");

      // requirementsオブジェクトを直接送信し、session_idはrequest bodyに含める
      const response = await axios.post(`${API_BASE_URL}/job_posting/regenerate`, 
        {
          ...requirements,
          session_id: sessionId
        }, 
        { withCredentials: true }
      );
      
      if (response.data.status === 'success') {
        const newJobPostMd = response.data.job_post;
        setJobPostMd(newJobPostMd);
        setIsDecided(false); // 再生成時は未確定に戻す

        // AIアドバイスを更新
        const advice = generateAdvice(newJobPostMd);
        setJobPostAdvice(advice);

        // 前回のAI品質チェック結果をクリア（新しい求人票なので）
        setAiAnalysis(null);

        // 再生成メッセージを追加
        setMessages(prevMessages => [
          ...prevMessages, 
          { 
            sender: 'bot', 
            text: '求人票が再生成されました。さらに質問や修正点があればお気軽にお知らせください。' 
          }
        ]);
        
        // 履歴に追加
        const newHistoryItem = {
          requirements: { ...requirements },
          jobPostMd: newJobPostMd,
          jobPostAdvice: advice,
          timestamp: new Date().toISOString(),
          is_decided: false
        };
        
        setJobPostHistory(prev => [...prev.slice(0, currentHistoryIndex + 1), newHistoryItem]);
        setCurrentHistoryIndex(prev => prev + 1);

        setMenuAnchorEl(null); // メニューの参照をリセット
        setUiMode('main');

        // 成功のお知らせ
        showSnackbar("求人票を再生成しました");
      } else {
        setError('求人票の再生成に失敗しました。もう一度お試しください。');
        setUiMode('main');
      }
    } catch (err) {
      console.error('Error regenerating job post:', err);
      showError(err, '求人票の再生成中にエラーが発生しました');
      setUiMode('main');
    } finally {
      setIsGenerating(false);
      endTracking();
    }
  };

  // (2) チャット送信
  const handleSendMessage = async () => {
    if (!userInput.trim() || !sessionId || isChatLoading) return;
    
    setIsChatLoading(true);

    const userMsg = { sender: 'user', text: userInput };
    setMessages((prev) => [...prev, userMsg]);
    setUserInput('');
    setIsTyping(true);

    try {
      // お知らせを表示
      showSnackbar("AIに質問を送信しました");
      
      const response = await axios.post(`${API_BASE_URL}/job_posting/chat`, 
        {
          session_id: sessionId,
          message: userMsg.text
        }, 
        { withCredentials: true }
      );
      
      if (response.data.status === 'success') {
        const botMsg = {
          sender: 'bot',
          text: response.data.response,
        };
        setMessages((prev) => [...prev, botMsg]);
      } else {
        // エラーメッセージの表示
        const errorMsg = {
          sender: 'bot',
          text: 'すみません、メッセージの処理中にエラーが発生しました。',
        };
        setMessages((prev) => [...prev, errorMsg]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      const errorMsg = {
        sender: 'bot',
        text: 'すみません、エラーが発生しました: ' + (err.message || '不明なエラー'),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
      setIsChatLoading(false);
    }
  };

  // 最初からやり直す
  const handleRestart = () => {
    if (window.confirm('入力内容をリセットして最初からやり直しますか？')) {
      setRequirements(defaultRequirements);
      setJobPostMd('');
      setSessionId(null);
      setMessages([]);
      setJobPostAdvice(null);
      setCreationMode(''); // 選択モードもリセット
      setIsDecided(false); // 確定状態もリセット
      setUiMode('selection'); // 選択画面に戻る
    }
  };
  
  // 履歴表示 - AssistMenuから履歴を開く
  const handleShowHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/job_posting/history`, {
        withCredentials: true
      });

      if (response.data && response.data.history) {
        setHistoryList(response.data.history);
        setHistoryDialogOpen(true);
      } else {
        showInfo('求人票の履歴がありません');
      }
    } catch (err) {
      console.error('履歴取得エラー:', err);
      showError(err, '履歴の取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };

  // 削除ボタンクリック時
  const handleDeleteClick = (item, event) => {
    event.stopPropagation(); // 親要素のクリックイベントを防止
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  // 削除実行
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);

    try {
      await axios.patch(
        `${API_BASE_URL}/job_posting/post/${itemToDelete.id}`,
        {},
        { withCredentials: true }
      );

      // 成功：履歴リストから削除
      setHistoryList(prev => prev.filter(item => item.id !== itemToDelete.id));

      // 削除したデータが現在表示中だった場合、ローカルストレージもクリア
      if (itemToDelete.session_id === sessionId) {
        localStorage.removeItem('jobPostCreationData');
      }

      // 成功メッセージ
      showInfo('履歴を削除しました');

    } catch (error) {
      console.error('削除エラー:', error);
      showError(error, '削除に失敗しました');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // 履歴から選択して読み込む
  const handleSelectHistoryItem = (historyItem) => {
    if (!historyItem) return;

    // 履歴読み込み時はunsaved表示を抑制
    isLoadingFromHistoryRef.current = true;

    // 履歴データからrequirementsとjobPostMdを復元
    const restoredRequirements = historyItem.requirement ? {
      role: historyItem.requirement.role || '',
      skills: historyItem.requirement.skills || '',
      salary: historyItem.requirement.salary || '',
      location: historyItem.requirement.location || '',
    } : defaultRequirements;

    setRequirements(restoredRequirements);
    setJobPostMd(historyItem.content || '');
    setSessionId(historyItem.session_id || null); // AIChatSessionのIDを使用
    setIsDecided(historyItem.is_decided || false); // 確定状態を復元

    // UIモードをmainに切り替え
    setMenuAnchorEl(null); // メニューの参照をリセット
    setUiMode('main');
    setHistoryDialogOpen(false);

    showSnackbar('過去の求人票を読み込みました');
  };

  // 履歴ダイアログを閉じる
  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setHistorySearchQuery('');
  };
  
  // Track job post decision
  const handleDecideJobPost = async () => {
    if (!sessionId) {
      alert("確定する求人票のIDが取得できません");
      return;
    }

    // 自動保存タイマーが残っていれば即時実行してキャンセル
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
      await autoSaveJobPost(jobPostMd);
    }

    const endTracking = await activityTracker.trackFeatureUse('job_post_decide', {
      jobPostMd,
      requirements
    });

    try {
      // APIを呼び出して求人票を確定する処理
      const response = await axios.post(`${API_BASE_URL}/job_posting/confirm`,
        {
          job_post: jobPostMd,
          requirements,
          session_id: sessionId
        },
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        // レスポンスの確定内容でフロントエンドの状態を同期
        if (response.data.job_post) {
          isSavingRef.current = true;
          setJobPostMd(response.data.job_post);
        }

        // 確定フラグを設定（常に更新）
        setIsDecided(true);

        // 現在の履歴アイテムを確定済みとしてマーク
        setJobPostHistory(prev =>
          prev.map((item, index) =>
            index === currentHistoryIndex
              ? { ...item, is_decided: true }
              : item
          )
        );

        // 確定成功
        showSnackbar('求人票を確定しました！');
      } else {
        setError('求人票の確定に失敗しました。もう一度お試しください。');
      }
    } catch (err) {
      console.error('Error confirming job post:', err);
      showError(err, '求人票の確定中にエラーが発生しました');
    } finally {
      endTracking();
    }
  };
  
  // 前の履歴に戻る
  const handlePrevVersion = () => {
    if (currentHistoryIndex > 0) {
      const prevIndex = currentHistoryIndex - 1;
      const prevItem = jobPostHistory[prevIndex];
      
      setRequirements(prevItem.requirements);
      setJobPostMd(prevItem.jobPostMd);
      setJobPostAdvice(prevItem.jobPostAdvice);
      setCurrentHistoryIndex(prevIndex);
    }
  };
  
  // 次の履歴に進む
  const handleNextVersion = () => {
    if (currentHistoryIndex < jobPostHistory.length - 1) {
      const nextIndex = currentHistoryIndex + 1;
      const nextItem = jobPostHistory[nextIndex];
      
      setRequirements(nextItem.requirements);
      setJobPostMd(nextItem.jobPostMd);
      setJobPostAdvice(nextItem.jobPostAdvice);
      setCurrentHistoryIndex(nextIndex);
    }
  };
  
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // 履歴選択
  const handleHistorySelect = (index) => {
    if (index >= 0 && index < jobPostHistory.length) {
      const selectedItem = jobPostHistory[index];
      setRequirements(selectedItem.requirements);
      setJobPostMd(selectedItem.jobPostMd);
      setJobPostAdvice(selectedItem.jobPostAdvice);
      setCurrentHistoryIndex(index);
      
      // 選択通知
      showSnackbar(`バージョン ${index + 1} の求人票を表示しています`);
    }
  };

  // Snackbar通知
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // チャットモード切替ボタンのハンドラーを更新
  const handleChatModeChange = () => {
    if (chatMode === 'sidebar') {
      setChatMode('popup');
    } else if (chatMode === 'popup') {
      setChatMode('off');
    } else {
      setChatMode('sidebar');
    }
  };

  // =========================================
  // 自動保存関数
  // =========================================
  const autoSaveJobPost = async (updatedMarkdown) => {
    if (!sessionId) return;

    try {
      setSaveStatus('saving');

      const response = await axios.post(`${API_BASE_URL}/job_posting/update`,
        {
          job_post: updatedMarkdown,
          session_id: sessionId
        },
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        setSaveStatus('saved');
        // 保存済み表示は2秒後に自動的に消す
        setTimeout(() => {
          setSaveStatus('');
        }, 2000);
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('自動保存に失敗しました:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('unsaved'), 5000);
    }
  };

  // jobPostMdの変更を監視して自動保存
  useEffect(() => {
    if (!sessionId || !jobPostMd) return;

    // 履歴読み込み時はunsaved表示をスキップ
    if (isLoadingFromHistoryRef.current) {
      isLoadingFromHistoryRef.current = false;
      setSaveStatus('');
      return;
    }

    // 保存完了後のsetJobPostMdによる再トリガーをスキップ
    if (isSavingRef.current) {
      isSavingRef.current = false;
      return;
    }

    setSaveStatus('unsaved');

    // 既存のタイマーをクリア
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // 3秒後に自動保存を実行
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveJobPost(jobPostMd);
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [jobPostMd, sessionId]);

  // =========================================
  // ジョブポスト更新ハンドラー（必要な場合はAPIへの保存も行う）
  // =========================================
  // Track job post update
  const handleJobPostUpdate = async (updatedMarkdown) => {
    const endTracking = await activityTracker.trackFeatureUse('job_post_update', {
      jobPostMd: updatedMarkdown
    });

    try {
      // バックエンドに保存する必要がある場合
      if (sessionId) {
        // 保存処理中の通知
        showSnackbar("変更内容を保存しています...");

        // APIを呼び出して更新
        const response = await axios.post(`${API_BASE_URL}/job_posting/update`, 
          {
            job_post: updatedMarkdown,
            session_id: sessionId
          }, 
          { withCredentials: true }
        );
        
        if (response.data.status === 'success') {
          // 応答からジョブポストを取得（サーバーが若干変更する可能性があるため）
          const savedJobPostMd = response.data.job_post || updatedMarkdown;
          
          // ローカルのステートを更新（useEffectの再トリガーを防止）
          isSavingRef.current = true;
          setJobPostMd(savedJobPostMd);

          // 履歴に追加
          const newHistoryItem = {
            requirements: { ...requirements },
            jobPostMd: savedJobPostMd,
            jobPostAdvice: jobPostAdvice,
            timestamp: new Date().toISOString(),
            is_decided: false
          };
          
          setJobPostHistory(prev => [...prev.slice(0, currentHistoryIndex + 1), newHistoryItem]);
          setCurrentHistoryIndex(prev => prev + 1);
          
          // 成功のお知らせ
          showSnackbar("求人票を保存しました");
        } else {
          setError('求人票の保存に失敗しました。もう一度お試しください。');
        }
      } else {
        // セッションIDがない場合はローカルだけ更新
        setJobPostMd(updatedMarkdown);
      }
    } catch (err) {
      console.error('Error saving job post:', err);
      showError(err, '求人票の保存中にエラーが発生しました');
    } finally {
      endTracking();
    }
  };

  // レンダリング
  return (
    <ThemeProvider theme={theme}>
    <AppLayout>
      <Box sx={{
        marginRight: !isSmDown && chatMode === 'sidebar' ? '400px' : 0,
        transition: 'margin-right 0.3s ease',
        backgroundColor: bg(theme).card
      }}>

            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {/* AIアシスト機能ボタン */}
            {uiMode === 'main' && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Tooltip
                  title={
                    chatMode === 'off' ? 'AIに相談' :
                    chatMode === 'sidebar' ? 'サイドバーチャット（クリックでポップアップに切替）' :
                    'ポップアップチャット（クリックでオフに切替）'
                  }
                  arrow
                >
                  <IconButton
                    onClick={handleChatModeChange}
                    sx={chatIconButton('job_posting', chatMode !== 'off')}
                  >
                    <ForumIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="その他の操作" arrow>
                  <IconButton
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                    sx={menuIconButton('job_posting')}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
              {uiMode === 'main' && (
                <>
                  {/* ステータスバー */}
                  <AnimatePresence>
                    {jobPostMd && sessionId && (
                      <Alert
                        component={motion.div}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        severity={isDecided ? "success" : "info"}
                        icon={isDecided ? <CheckCircleOutlineIcon /> : <InfoOutlinedIcon />}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          '& .MuiAlert-message': {
                            width: '100%'
                          }
                        }}
                      >
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {isDecided ? 'この求人票は確定済みです' : 'この求人票は未確定です'}
                          </Typography>
                          <Typography variant="body2">
                            {isDecided
                              ? '確定済みの求人票は選定した媒体への配信準備が完了しています。必要に応じて修正も可能です'
                              : '確定すると掲載準備が完了し、選定した媒体への配信がスムーズに進められます'}
                          </Typography>
                        </Box>
                      </Alert>
                    )}
                  </AnimatePresence>

                  <MainContent
                    requirements={requirements}
                    setRequirements={setRequirements}
                    jobPostMd={jobPostMd}
                    setJobPostMd={handleJobPostUpdate}
                    jobPostAdvice={jobPostAdvice}
                    isEditingRequirements={isEditingRequirements}
                    setIsEditingRequirements={setIsEditingRequirements}
                    onRequirementsUpdate={handleRequirementsSave}
                    chatMode={chatMode}
                    aiAnalysis={aiAnalysis}
                    setAiAnalysis={setAiAnalysis}
                  />
                  <AssistMenu
                    onResetClick={() => {
                      setMenuAnchorEl(null); // メニューを閉じる
                      setCreationMode(''); // creationModeをリセット
                      setUiMode('selection'); // 選択画面に戻る
                    }}
                    onRegenerateClick={handleRegenerateJobPost}
                    onDecideJobPost={handleDecideJobPost}
                    onShowHistory={handleShowHistory}
                    requirements={requirements}
                    jobPostMd={jobPostMd}
                    jobPostHistory={jobPostHistory}
                    currentHistoryIndex={currentHistoryIndex}
                    onHistorySelect={handleHistorySelect}
                    onPrevVersion={currentHistoryIndex > 0 ? handlePrevVersion : null}
                    onNextVersion={currentHistoryIndex < jobPostHistory.length - 1 ? handleNextVersion : null}
                    anchorEl={menuAnchorEl}
                    onClose={() => setMenuAnchorEl(null)}
                  />

                  {/* 自動保存ステータス表示 - 必要な時だけ表示 */}
                  {sessionId && saveStatus && saveStatus !== '' && (
                    <Box
                      sx={{
                        position: 'fixed',
                        top: 140,
                        right: 24,
                        zIndex: 900,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: bg(theme).card,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        boxShadow: 2,
                      }}
                    >
                      {saveStatus === 'saving' && (
                        <>
                          <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            保存中...
                          </Typography>
                        </>
                      )}
                      {saveStatus === 'saved' && (
                        <>
                          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                            保存済み
                          </Typography>
                        </>
                      )}
                      {saveStatus === 'unsaved' && (
                        <>
                          <SaveIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          <Typography variant="caption" sx={{ color: 'warning.main' }}>
                            未保存の変更
                          </Typography>
                        </>
                      )}
                      {saveStatus === 'error' && (
                        <>
                          <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          <Typography variant="caption" sx={{ color: 'error.main' }}>
                            保存エラー
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}

                  {/* FAB - 未確定の場合のみ表示 */}
                  {jobPostMd && sessionId && !isDecided && (
                    <Fab
                      component={motion.button}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 260, damping: 20 }}
                      variant="extended"
                      onClick={handleDecideJobPost}
                      sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          backgroundColor: '#43a047',
                          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CheckIcon sx={{ mr: 1 }} />
                      この求人票を確定
                    </Fab>
                  )}
                </>
              )}

              {/* 作成モード選択画面 */}
              {uiMode === 'selection' && (
                <Container maxWidth="lg" sx={{ py: 6 }}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    sx={{ textAlign: 'center', mb: 6 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        color: jobPostTheme.primary,
                      }}
                    >
                      求人票の作成方法を選択
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      以下の3つの方法から求人票の作成方法を選んでください
                    </Typography>
                  </Box>

                  {/* 品質チェック機能の説明 */}
                  <Box
                    sx={{
                      maxWidth: 600,
                      mx: 'auto',
                      mb: 5,
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: bg(theme).subtle,
                      border: `1px solid ${border(theme).subtle}`,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).primary, mb: 1.5, textAlign: 'center' }}>
                      二段構えの品質チェックで求人票を改善
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <SpeedIcon sx={{ fontSize: 15, color: textColors(theme).muted }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                            リアルタイムチェック
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: textColors(theme).muted, lineHeight: 1.5, display: 'block' }}>
                          編集と同時に自動でスコアリング。文字数・読みやすさ・給与情報など12軸を即座に評価します。
                        </Typography>
                      </Box>
                      <Box sx={{ width: '1px', backgroundColor: border(theme).subtle, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <AutoAwesomeIcon sx={{ fontSize: 15, color: textColors(theme).muted }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                            AI品質チェック
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: textColors(theme).muted, lineHeight: 1.5, display: 'block' }}>
                          ボタンひとつでAIが深く分析。文脈・トーン・論理性・訴求力など意味的な品質を評価します。
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Grid container spacing={4} justifyContent="center" sx={{ mb: 4 }}>
                    {/* 初めから作成カード */}
                    <Grid item xs={12} md={4}>
                      <Paper
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        whileHover={{ y: -8 }}
                        onClick={() => handleCreationModeSelect('new')}
                        sx={{
                          ...selectionCard('job_posting', theme),
                          minHeight: 400,
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                        }}
                      >
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                          {/* Icon */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '12px',
                                backgroundColor: `${jobPostTheme.primary}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <RestartAltIcon sx={{ fontSize: 32, color: jobPostTheme.primary }} />
                            </Box>
                          </Box>

                          {/* Content */}
                          <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 600,
                                color: textColors(theme).primary,
                                mb: 2,
                              }}
                            >
                              初めから作成
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                mb: 3,
                              }}
                            >
                              基本情報から新しい求人票を作成します。必要な情報を入力して、AIが最適な求人票を生成します。
                            </Typography>

                            <Box sx={{
                              p: 2,
                              bgcolor: `${jobPostTheme.primary}08`,
                              borderRadius: 2,
                              textAlign: 'left'
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, color: jobPostTheme.primary, fontWeight: 600 }}>
                                主な入力項目:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {['募集職種', '必要なスキル', '給与', '勤務地', '求める人材像', '会社PR'].map((item) => (
                                  <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: jobPostTheme.primary, mr: 0.75 }}/>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>

                          {/* Action Button */}
                          <Button
                            variant="contained"
                            sx={{
                              ...selectionCardButton('job_posting'),
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreationModeSelect('new');
                            }}
                          >
                            この方法で作成する
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* 過去の求人票をもとに作成カード */}
                    <Grid item xs={12} md={4}>
                      <Paper
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        whileHover={{ y: -8 }}
                        onClick={() => handleCreationModeSelect('existing')}
                        sx={{
                          ...selectionCard('job_posting', theme),
                          minHeight: 400,
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                        }}
                      >
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                          {/* Icon */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '12px',
                                backgroundColor: `${jobPostTheme.primary}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <HistoryIcon sx={{ fontSize: 32, color: jobPostTheme.primary }} />
                            </Box>
                          </Box>

                          {/* Content */}
                          <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 600,
                                color: textColors(theme).primary,
                                mb: 2,
                              }}
                            >
                              過去の求人票をもとに作成
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                mb: 3,
                              }}
                            >
                              過去の求人票PDFをアップロードするか、情報を手動入力して新しい求人票を作成します。AIが最適化します。
                            </Typography>

                            <Box sx={{
                              p: 2,
                              bgcolor: `${jobPostTheme.primary}08`,
                              borderRadius: 2,
                              textAlign: 'left'
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, color: jobPostTheme.primary, fontWeight: 600 }}>
                                主な入力項目:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {['PDFアップロード', 'タイトル', '概要', '仕事内容', '求める人材'].map((item) => (
                                  <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: jobPostTheme.primary, mr: 0.75 }}/>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>

                          {/* Action Button */}
                          <Button
                            variant="contained"
                            sx={{
                              ...selectionCardButton('job_posting'),
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreationModeSelect('existing');
                            }}
                          >
                            この方法で作成する
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* 過去の履歴から再開カード */}
                    <Grid item xs={12} md={4}>
                      <Paper
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        whileHover={{ y: -8 }}
                        onClick={() => {
                          // 履歴ダイアログを開く
                          handleShowHistory();
                        }}
                        sx={{
                          ...selectionCard('job_posting', theme),
                          minHeight: 400,
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                        }}
                      >
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                          {/* Icon */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '12px',
                                backgroundColor: `${jobPostTheme.primary}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <AssignmentIcon sx={{ fontSize: 32, color: jobPostTheme.primary }} />
                            </Box>
                          </Box>

                          {/* Content */}
                          <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 600,
                                color: textColors(theme).primary,
                                mb: 2,
                              }}
                            >
                              過去の履歴から再開
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                mb: 3,
                              }}
                            >
                              過去に作成した求人票を確認・再利用します。履歴から選択して、必要に応じて修正できます。
                            </Typography>

                            <Box sx={{
                              p: 2,
                              bgcolor: `${jobPostTheme.primary}08`,
                              borderRadius: 2,
                              textAlign: 'left'
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, color: jobPostTheme.primary, fontWeight: 600 }}>
                                できること:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {['過去の求人票を確認', '決定済み求人票を再利用', '求人票を修正', '履歴を検索'].map((item) => (
                                  <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: jobPostTheme.primary, mr: 0.75 }}/>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>

                          {/* Action Button */}
                          <Button
                            variant="contained"
                            sx={{
                              ...selectionCardButton('job_posting'),
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowHistory();
                            }}
                          >
                            この方法で選択する
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Container>
              )}

              {/* 初期入力フォーム */}
              {uiMode === 'form' && (
                <InitialFormScreen
                  creationMode={creationMode}
                  defaultValues={requirements}
                  onSubmit={handleFormSubmit}
                  isLoading={isLoading}
                  onBack={() => { setUiMode('selection'); setCreationMode(''); }}
                />
              )}
              
              {uiMode === 'loading' && (
                isGenerating ? (
                  <LoadingScreen
                    variant="overlay"
                    message="AIが求人票を再生成しています..."
                    description="AIが最適な求人票を作成するために、データを分析しています。少々お待ちください。"
                    loadingType="job_posting"
                    featureKey="job_posting"
                  />
                ) : (
                  <LoadingScreen
                    message="AIが最適な求人を生成しています..."
                    loadingType="job_posting"
                    featureKey="job_posting"
                  />
                )
              )}
            </Box>
      </Box>

        {/* チャット (サイドバー/ポップアップ) */}
        {uiMode === 'main' && chatMode === 'popup' && (
          <AIChat
            messages={messages}
            userInput={userInput}
            setUserInput={setUserInput}
            isTyping={isTyping}
            isLoading={isGenerating}
            chatLoading={isChatLoading}
            onChatSend={handleSendMessage}
            onReflectPlan={handleRegenerateJobPost}
            chatEndRef={chatEndRef}
            initialGuideMessage="ここでは求人票に関する質問を入力できます。"
            iconTooltipMessage="求人票をAIと相談！"
            chatButtonLabel="AIに相談"
            planButtonLabel="求人票を修正"
            featureKey="job_posting"
          />
        )}
        {uiMode === 'main' && chatMode === 'sidebar' && (
          <AIChatSide
            messages={messages}
            userInput={userInput}
            setUserInput={setUserInput}
            isTyping={isTyping}
            isLoading={isGenerating}
            chatLoading={isChatLoading}
            onChatSend={handleSendMessage}
            onReflectPlan={handleRegenerateJobPost}
            chatEndRef={chatEndRef}
            initialGuideMessage="サイドチャットでいつでもAIに相談できます。"
            onClose={() => setChatMode('popup')}
            chatButtonLabel="AIに相談"
            planButtonLabel="求人票を修正"
            featureKey="job_posting"
          />
        )}

        {/* 過去履歴ダイアログ - Timeline形式 */}
        <Dialog
          open={historyDialogOpen}
          onClose={handleCloseHistoryDialog}
          fullWidth
          maxWidth="md"
        >
          {/* ヘッダー */}
          <Box
            sx={{
              backgroundColor: '#4CAF50',
              color: 'white',
              px: 3,
              py: 3
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              過去の求人票履歴
            </Typography>

            {/* 検索バー */}
            <TextField
              fullWidth
              placeholder="職種、キーワードで検索..."
              size="small"
              value={historySearchQuery}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'white' }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: alpha('#ffffff', 0.2),
                  borderRadius: 3,
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover': {
                    backgroundColor: alpha('#ffffff', 0.25)
                  },
                  '& input::placeholder': {
                    color: alpha('#ffffff', 0.8),
                    opacity: 1
                  },
                  '& input': {
                    color: 'white'
                  }
                }
              }}
            />
          </Box>

          <DialogContent sx={{ p: 3, backgroundColor: bg(theme).subtle }}>
            {historyLoading ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress sx={{ color: jobPostTheme.primary }} />
                <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                  履歴を読み込んでいます...
                </Typography>
              </Box>
            ) : historyList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <AccessTimeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  履歴がありません
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                {/* Timeline縦線 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 24,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    backgroundColor: '#4CAF50',
                    opacity: 0.25
                  }}
                />

                {/* Timeline Items */}
                <AnimatePresence>
                  {historyList
                    .filter((item) => {
                      if (!historySearchQuery.trim()) return true;
                      const query = historySearchQuery.toLowerCase();
                      const title = (item.title || '').toLowerCase();
                      const role = (item.requirement?.role || '').toLowerCase();
                      const content = (item.content || '').toLowerCase();
                      const itemId = String(item.id || '');

                      return title.includes(query) ||
                             role.includes(query) ||
                             content.includes(query) ||
                             itemId.includes(query);
                    })
                    .map((item, index) => {
                      const isCurrent = sessionId && item.session_id && (sessionId === item.session_id);
                      const title = item.title || item.requirement?.role || '求人票';
                      const itemDate = new Date(item.created_at || item.updated_at);

                      return (
                        <Box
                          key={item.id}
                          component={motion.div}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{
                            duration: 0.4,
                            delay: index * 0.1,
                            ease: [0.4, 0, 0.2, 1]
                          }}
                          sx={{ position: 'relative', pl: 7, pb: 4, '&:last-child': { pb: 0 } }}
                        >
                          {/* Timeline Icon */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 12,
                              top: 0,
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: '#4CAF50',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                              zIndex: 1
                            }}
                          >
                            {item.is_decided ? (
                              <CheckIcon sx={{ fontSize: 14, color: 'white' }} />
                            ) : (
                              <AccessTimeIcon sx={{ fontSize: 14, color: 'white' }} />
                            )}
                          </Box>

                          {/* Timeline Card */}
                          <Box
                            component={motion.div}
                            whileHover={{ y: -4, transition: { duration: 0.2 } }}
                            sx={{
                              position: 'relative',
                              backgroundColor: bg(theme).card,
                              borderRadius: 2,
                              p: 2.5,
                              boxShadow: shadow(theme).sm,
                              border: '1px solid',
                              borderColor: item.is_decided ? 'success.light' : 'divider',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                borderColor: item.is_decided ? 'success.main' : 'primary.main'
                              }
                            }}
                          >
                            {/* Card Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <AssignmentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                    {title}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {itemDate.toLocaleDateString('ja-JP', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    timeZone: 'Asia/Tokyo'
                                  })}
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {item.is_decided && (
                                  <Chip
                                    label="確定済み"
                                    size="small"
                                    icon={<CheckIcon />}
                                    sx={{
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontWeight: 600,
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                    }}
                                  />
                                )}
                                {isCurrent && (
                                  <Chip
                                    label="表示中"
                                    size="small"
                                    sx={{
                                      bgcolor: alpha('#4CAF50', 0.1),
                                      color: '#4CAF50',
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            {/* Card Content */}
                            <Box sx={{ mt: 2 }}>
                              {item.requirement && (
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                  {item.requirement.role && (
                                    <Box sx={{ flex: 1, minWidth: 150 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        募集職種
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {item.requirement.role}
                                      </Typography>
                                    </Box>
                                  )}
                                  {item.requirement.location && (
                                    <Box sx={{ flex: 1, minWidth: 150 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        勤務地
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {item.requirement.location}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                              {item.content && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    求人票プレビュー
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6, color: 'text.secondary' }}>
                                    {item.content.slice(0, 100)}...
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Card Actions */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2 }}>
                              <Button
                                variant={isCurrent ? "outlined" : "contained"}
                                size="small"
                                disabled={isCurrent}
                                onClick={!isCurrent ? () => handleSelectHistoryItem(item) : undefined}
                                sx={{
                                  borderRadius: 20,
                                  px: 3,
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  ...(!isCurrent && {
                                    backgroundColor: '#4CAF50',
                                    '&:hover': {
                                      backgroundColor: '#43a047',
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }
                                  })
                                }}
                              >
                                {isCurrent ? "表示中" : "この求人票を表示"}
                              </Button>

                              {/* 削除ボタン（現在表示中でない場合のみ表示） */}
                              {!isCurrent && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteClick(item, e)}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                </AnimatePresence>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, backgroundColor: bg(theme).subtle, borderTop: `1px solid ${border(theme).default}` }}>
            <Button onClick={handleCloseHistoryDialog} variant="outlined" sx={{ borderRadius: 20, px: 3 }}>
              閉じる
            </Button>
          </DialogActions>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              履歴を削除しますか？
            </Typography>

            {itemToDelete && (
              <>
                <Typography variant="body1" gutterBottom>
                  <strong>求人票 ID:</strong> {itemToDelete.id}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>作成日:</strong> {new Date(itemToDelete.created_at).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'Asia/Tokyo'
                  })}
                </Typography>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  削除した履歴は復元できません
                </Alert>

                {itemToDelete.is_decided && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    このデータは確定済みです。本当に削除しますか？
                  </Alert>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={isDeleting}
              >
                キャンセル
              </Button>
              <Button
                onClick={handleConfirmDelete}
                color="error"
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
              >
                {isDeleting ? '削除中...' : '削除する'}
              </Button>
            </Box>
          </Box>
        </Dialog>

        
        {/* 変更通知用のSnackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="info"
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
    </AppLayout>
    </ThemeProvider>
  );
}

/**
 * 生成された求人票カード
 * - Markdown表示 + テキストエリア編集 + コピーボタン
 */
function JobPostCard({ jobPostMd, onRegenerateClick }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(jobPostMd).then(() => {
      showSuccess('求人票をコピーしました！');
    }).catch(() => {
      showError(null, 'コピーに失敗しました');
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        maxWidth: 700,
        mx: 'auto',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 'bold', 
          mb: 2, 
          color: '#2e7d32',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            display: 'inline-block',
            width: '4px',
            height: '20px',
            backgroundColor: '#4CAF50',
            marginRight: '8px',
            borderRadius: '4px'
          }
        }}
      >
        生成された求人票
      </Typography>

      <Box
        sx={{
          border: (t) => `1px solid ${border(t).default}`,
          p: 3,
          borderRadius: 2,
          backgroundColor: (t) => bg(t).card,
          mb: 3,
          maxHeight: 400,
          overflowY: 'auto',
          boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)',
          '& h1, & h2': {
            borderBottom: (t) => `1px solid ${border(t).light}`,
            paddingBottom: '0.3em',
            marginBottom: '16px',
            fontWeight: 600,
          },
          '& h1': {
            fontSize: '1.8rem',
            color: (t) => t.palette.primary.main,
          },
          '& h2': {
            fontSize: '1.4rem',
            color: (t) => textColors(t).primary,
            marginTop: '24px',
          },
          '& ul, & ol': {
            paddingLeft: '2em',
          },
          '& a': {
            color: (t) => t.palette.primary.main,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& blockquote': {

            paddingLeft: '16px',
            margin: '16px 0',
            color: (t) => textColors(t).muted,
          },
          '& code': {
            backgroundColor: (t) => bg(t).tinted,
            borderRadius: '3px',
            padding: '0.2em 0.4em',
            fontFamily: 'monospace',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            margin: '16px 0',
          },
          '& th, & td': {
            border: (t) => `1px solid ${border(t).default}`,
            padding: '8px 12px',
          },
          '& th': {
            backgroundColor: (t) => bg(t).tinted,
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
          },
          '& strong': {
            fontWeight: 600,
          }
        }}
      >
        {jobPostMd ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{jobPostMd}</ReactMarkdown>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
          >
            まだ求人票は作成されていません。
          </Typography>
        )}
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        (必要に応じて直接修正してコピペできます)
      </Typography>
      <TextField
        multiline
        rows={8}
        fullWidth
        value={jobPostMd}
        onChange={(e) => setJobPostMd(e.target.value)}
        variant="outlined"
        sx={{ 
          mb: 2,
          '& .MuiOutlinedInput-root': {
            fontFamily: 'monospace',
            fontSize: '0.9rem',
          }
        }}
      />

      <Box sx={{ textAlign: 'right' }}>
        <Button
          variant="contained"
          startIcon={<AssignmentTurnedInIcon />}
          onClick={handleCopy}
          sx={{
            borderRadius: 9999,
            textTransform: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            backgroundColor: '#4CAF50',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              backgroundColor: '#43a047'
            }
          }}
        >
          コピー
        </Button>
      </Box>
    </Paper>
  );
}